<template>
  <div class="signing-app">
    <!-- App Header -->
    <app-header :title="pageTitle"></app-header>

    <!-- Progress Stepper -->
    <progress-stepper :current-step="2"></progress-stepper>

    <!-- Enhanced Loading Overlay -->
    <loading-overlay
        :show="!documentFullyLoaded"
        :message="loadingMessage"
    ></loading-overlay>

    <!-- Main Content - Only show when document is fully loaded -->
    <div class="main-content" v-if="documentFullyLoaded && !error">
      <!-- Document Viewer -->
      <document-viewer
          :document-url="documentUrl"
          :title="documentTitle"
          @error="handleViewerError"
          @expired-url="handleExpiredUrl"
          @document-loaded="handleDocumentLoaded"
      ></document-viewer>

      <!-- Signature Preview (when available) -->
      <div v-if="signaturePreview" class="signature-preview-section">
        <div class="preview-header">
          <h3>請確認你的簽名</h3>
          <p>確認你的簽名清晰且完整</p>
        </div>

        <div class="preview-container">
          <img :src="signaturePreview" alt="你的簽名" class="signature-image" />
        </div>

        <div class="preview-actions">
          <button @click="redoSignature" class="secondary-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
            </svg>
            重新簽署
          </button>
          <button @click="confirmSignature" class="primary-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            確認並提交
          </button>
        </div>
      </div>

      <!-- Signature Component (when no preview is showing) -->
      <div v-if="!signaturePreview">
        <signature-component
            :prompt-title="'請簽署本協議'"
            :prompt-message="'請點擊下方「開始簽署」按鈕，在簽名板上簽署你的<strong>完整姓名</strong>，以完成本協議。'"
            :complete-button-text="'完成簽署'"
            @signature-completed="handleSignatureCompletion"
        ></signature-component>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-container">
      <div class="error-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h2>發生問題</h2>
      <p>{{ error }}</p>
      <button @click="retryOperation" class="retry-btn">
        重試
      </button>
    </div>
  </div>
</template>

<script>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';

// Import components
import AppHeader from '@/components/AppHeader.vue';
import ProgressStepper from '@/components/ProgressStepper.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import DocumentViewer from '@/components/DocumentViewer.vue';
import SignatureComponent from '@/components/SignatureComponent.vue';

// Import services
import pdfService from '@/services/pdf.service';
import signatureService from '@/services/signature.service';

export default {
  name: 'SignDocument',
  components: {
    AppHeader,
    ProgressStepper,
    LoadingOverlay,
    DocumentViewer,
    SignatureComponent
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Reactive state
    const loading = ref(true);
    const loadingMessage = ref('貸款合約正在載入，請不要離開此頁面...');
    const error = ref(null);
    const documentUrl = ref('');
    const documentTitle = ref('貸款合約');
    const documentId = ref('');
    const documentToken = ref('');
    const pageTitle = ref('簽署貸款合約');
    const documentLoaded = ref(false);
    const urlExpiryTime = ref(null);
    const urlRefreshTimer = ref(null);
    const retryCount = ref(0);
    const maxRetries = 3;
    const signaturePreview = ref(null);

    // Add a delay to ensure the loading overlay is visible long enough for users to see
    const loadingDelay = ref(true);

    // Compute a single value to determine if document is fully ready to display
    const documentFullyLoaded = computed(() => {
      return !loading.value && documentLoaded.value;
    });

    /**
     * Initialize the signing process
     */
    const initialize = async () => {
      // Show loading overlay immediately
      loading.value = true;
      loadingDelay.value = false; // Remove artificial delay
      documentLoaded.value = false;
      error.value = null;
      loadingMessage.value = '貸款合約正在載入，請不要離開此頁面...';
      signaturePreview.value = null;

      try {
        // Get document token from route params
        documentToken.value = route.params.token || '';

        // Fetch document URL and metadata
        await fetchDocumentData();

        // No artificial delay needed
        documentLoaded.value = true;
        loading.value = false;
      } catch (err) {
        console.error('初始化錯誤:', err);
        error.value = err.message || '無法載入簽署頁面。請稍後再試。';
        loading.value = false;
        documentLoaded.value = false;
      }
    };

    const handleExpiredUrl = async (message) => {
      console.warn('文檔URL可能已過期：', message);
      loadingMessage.value = '重新獲取文檔訪問權限...';
      loading.value = true;

      try {
        // Clear any existing refresh timer
        if (urlRefreshTimer.value) {
          clearTimeout(urlRefreshTimer.value);
          urlRefreshTimer.value = null;
        }

        // Try to get a fresh URL
        const freshUrl = await pdfService.getDocumentUrlFromToken(documentToken.value);
        console.log('成功刷新文檔URL');

        // Update the document URL
        documentUrl.value = freshUrl;

        // Set the URL expiry time (default to 55 minutes to be safe)
        setupUrlRefreshTimer(55 * 60);

        // Reset loading state
        loading.value = false;
        loadingDelay.value = false;
      } catch (err) {
        console.error('刷新過期URL失敗:', err);
        error.value = '文檔訪問鏈接已過期，無法自動刷新。請重試或聯繫客戶支持。';
        loading.value = false;
        loadingDelay.value = false;
      }
    };

// Add this function to set up automated URL refresh
    const setupUrlRefreshTimer = (expiresInSeconds = 3600) => {
      // Clear any existing timer
      if (urlRefreshTimer.value) {
        clearTimeout(urlRefreshTimer.value);
      }

      // Calculate refresh time (at 80% of expiry)
      const refreshDelay = Math.floor(expiresInSeconds * 0.8) * 1000;
      urlExpiryTime.value = Date.now() + (expiresInSeconds * 1000);

      console.log(`設置URL刷新計時器，將在 ${Math.round(refreshDelay/1000/60)} 分鐘后刷新`);

      // Set timer to refresh before URL expires
      urlRefreshTimer.value = setTimeout(async () => {
        console.log('自動刷新文檔URL');
        try {
          documentUrl.value = await pdfService.getDocumentUrlFromToken(documentToken.value);

          // Reset the timer for the next cycle
          setupUrlRefreshTimer(expiresInSeconds);
        } catch (err) {
          console.error('自動刷新URL失敗:', err);
          // Try again in 30 seconds if refresh fails
          urlRefreshTimer.value = setTimeout(() => {
            handleExpiredUrl('自動刷新失敗，嘗試手動刷新');
          }, 30000);
        }
      }, refreshDelay);
    };

// Update fetchDocumentData to use setupUrlRefreshTimer
    const fetchDocumentData = async () => {
      try {
        // Get direct URL to document using token
        documentUrl.value = await pdfService.getDocumentUrlFromToken(documentToken.value);
        console.log('成功獲取文檔直接訪問URL');

        // Setup refresh timer (assume 1 hour expiry by default)
        setupUrlRefreshTimer(3600);

        documentTitle.value = '貸款合約';
      } catch (err) {
        await router.push({
          path: '/error'
        });
      }
    };

    /**
     * Handle errors from the document viewer
     */
    const handleViewerError = (message) => {
      console.error('文檔查看器錯誤:', message);
      error.value = message;
      loading.value = false;
      documentLoaded.value = false;
      loadingDelay.value = false;
    };

    /**
     * Handle document loaded event
     */
    const handleDocumentLoaded = (data) => {
      console.log('Document loaded successfully:', data);
      documentLoaded.value = true;
      // Update loading message in case document is loaded but still processing
      loadingMessage.value = '文件準備完成，請稍候...';
    };

    /**
     * Handle signature completion from SignatureComponent
     */
    const handleSignatureCompletion = (signatureData) => {
      console.log('簽名完成，顯示預覽');
      signaturePreview.value = signatureData;

      // Scroll to the preview section
      nextTick(() => {
        const previewElement = document.querySelector('.signature-preview-section');
        if (previewElement) {
          previewElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    };

    /**
     * Redo the signature (clear preview and allow user to sign again)
     */
    const redoSignature = () => {
      signaturePreview.value = null;
    };

    /**
     * Confirm and submit the signature
     */
    const confirmSignature = async () => {
      if (!signaturePreview.value) {
        return;
      }

      loading.value = true;
      loadingMessage.value = '正在提交你的簽名...';
      error.value = null;

      try {
        console.log('Starting signature submission process...');
        console.log('Document token:', documentToken.value);
        console.log('Signature preview length:', signaturePreview.value.length);

        // Submit signature to backend
        console.log('Calling signatureService.submitSignature()...');
        const response = await signatureService.submitSignature(
            documentToken.value,
            signaturePreview.value
        );

        console.log('Response received:', response);

        // The response should contain agreementId, loanRef, and timestamp
        // based on our SignatureService implementation
        localStorage.setItem('signedAgreementId', response.agreementId);
        localStorage.setItem('signedLoanRef', response.loanRef);
        localStorage.setItem('signatureTimestamp', response.timestamp);
        localStorage.setItem('signatureSuccess', 'true');

        console.log('Stored data in localStorage, redirecting to success page...');

        // Navigate to success page with the agreement ID
        await router.push({
          path: '/success',
          query: {
            id: response.agreementId
          }
        });
      } catch (err) {
        console.error('Signature submission error details:', err);
        console.error('Error stack:', err.stack);
        error.value = `簽名提交失敗: ${err.message || '未知錯誤'}`;
        loading.value = false;
        loadingDelay.value = false;
      }
    };

    /**
     * Retry the current operation after an error
     */
    const retryOperation = () => {
      if (retryCount.value >= maxRetries) {
        error.value = '已達到最大重試次數。請稍後再試或聯繫客戶支持。';
        return;
      }

      retryCount.value++;
      initialize();
    };

    // Initialize when component is mounted
    onMounted(() => {
      initialize();

      // Keep the safety timeout but update the check
      setTimeout(() => {
        if (loading.value) {
          console.log('Safety timeout reached - forcing document to show');
          loading.value = false;
          documentLoaded.value = true;
        }
      }, 5000); // Reduced from 10s to 5s
    });

    onBeforeUnmount(() => {
      if (urlRefreshTimer.value) {
        clearTimeout(urlRefreshTimer.value);
        urlRefreshTimer.value = null;
      }
    });

    // Return values and methods for template
    return {
      loading,
      loadingMessage,
      error,
      documentUrl,
      documentTitle,
      documentId,
      documentToken,
      pageTitle,
      documentLoaded,
      documentFullyLoaded, // Add the computed property to the returned object
      signaturePreview,
      handleViewerError,
      handleDocumentLoaded,
      handleSignatureCompletion,
      redoSignature,
      confirmSignature,
      handleExpiredUrl,
      setupUrlRefreshTimer,
      retryOperation
    };
  }
};
</script>

<style scoped>
.signing-app {
  display: flex;
  flex-direction: column;
  min-height: 80vh;
  padding: 0 16px 24px;
  background-color: #f8f9fa;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

/* Signature Preview Section */
.signature-preview-section {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.preview-header {
  margin-bottom: 16px;
  text-align: center;
}

.preview-header h3 {
  color: #333333;
  font-weight: 600;
  font-size: 16px;
  margin: 0 0 4px;
}

.preview-header p {
  color: #666666;
  font-size: 14px;
  margin: 0;
}

.preview-container {
  background-color: #f8f9fa;
  border: 1px solid #dadce0;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  min-height: 120px;
}

.signature-image {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}

.preview-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.secondary-btn, .primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  font-size: 14px;
  -webkit-tap-highlight-color: transparent;
}

.secondary-btn {
  background-color: white;
  color: #666666;
  border: 1px solid #dadce0;
}

.secondary-btn:active {
  background-color: #f8f9fa;
}

.primary-btn {
  background-color: #FF9E15;
  color: #333333;
  border: none;
}

.primary-btn:active {
  background-color: #E68A00;
}

.button-icon {
  flex-shrink: 0;
}

/* Error Container */
.error-container {
  margin: 48px auto;
  max-width: 400px;
  text-align: center;
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-icon {
  color: #ea4335;
  margin-bottom: 16px;
}

.error-container h2 {
  margin: 0 0 12px;
  color: #ea4335;
  font-size: 18px;
  font-weight: 500;
}

.error-container p {
  margin: 0 0 24px;
  color: #5f6368;
  font-size: 14px;
}

.retry-btn {
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 24px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background-color: #1765cc;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .signing-app {
    padding: 0 12px 16px;
  }

  .signature-preview-section,
  .error-container {
    padding: 16px;
  }

  .preview-actions {
    flex-direction: column;
    gap: 8px;
  }

  .secondary-btn, .primary-btn {
    width: 100%;
  }
}

@media (min-width: 768px) {
  .preview-container {
    min-height: 150px;
  }

  .signature-image {
    max-height: 180px;
  }
}

@media (min-width: 1200px) {
  .signing-app {
    padding: 0 40px 40px;
  }

  .signature-preview-section {
    max-width: 600px;
    margin: 0 auto 20px;
  }
}
</style>